import { getLastCertVerifyFailure } from "@/lib/certified-analytics";
import { getEnvMap, getIcConfig, hasProtocolEnv } from "@/lib/network";

export type RuntimeDiagnostics = {
	hasProtocolEnv: boolean;
	protocolId: string;
	host: string;
	network: string;
	fetchRootKey: boolean;
	/** Last analytics cert-verify failure (empty_certificate | no_root_key | …). */
	certVerifyFailure: string | null;
	ledgers: {
		usdm: boolean;
		mmk: boolean;
		icp: boolean;
		ckBtc: boolean;
		ckEth: boolean;
		ckUsdc: boolean;
	};
	indexes: { usdm: boolean; mmk: boolean };
	gateway: boolean;
	assistant: boolean;
	faucet: boolean;
	mode: string;
	isViteDev: boolean;
};

/** Snapshot of env wiring (public canister ids only). */
export function getRuntimeDiagnostics(): RuntimeDiagnostics {
	const env = getEnvMap();
	let host = env.VITE_IC_HOST ?? "";
	let network = "";
	let fetchRootKey = false;
	if (hasProtocolEnv()) {
		try {
			const c = getIcConfig();
			host = c.host;
			network = c.network;
			fetchRootKey = c.fetchRootKey;
		} catch {
			/* ignore */
		}
	}

	const proto = (
		env.VITE_CANISTER_ID_PROTOCOL ||
		env.VITE_PROTOCOL_CANISTER_ID ||
		""
	).trim();

	return {
		hasProtocolEnv: hasProtocolEnv(),
		protocolId: proto,
		host,
		network,
		fetchRootKey,
		certVerifyFailure: getLastCertVerifyFailure(),
		ledgers: {
			usdm: Boolean(env.VITE_CANISTER_ID_USDM_LEDGER?.trim()),
			mmk: Boolean(env.VITE_CANISTER_ID_MMK_LEDGER?.trim()),
			icp: Boolean(
				env.VITE_ICP_LEDGER_CANISTER_ID?.trim() ||
					env.VITE_CANISTER_ID_DEMO_ICP_LEDGER?.trim(),
			),
			ckBtc: Boolean(
				env.VITE_ckBTC_LEDGER_CANISTER_ID?.trim() ||
					env.VITE_CANISTER_ID_DEMO_CKBTC_LEDGER?.trim(),
			),
			ckEth: Boolean(
				env.VITE_ckETH_LEDGER_CANISTER_ID?.trim() ||
					env.VITE_CANISTER_ID_DEMO_CKETH_LEDGER?.trim(),
			),
			ckUsdc: Boolean(
				env.VITE_ckUSDC_LEDGER_CANISTER_ID?.trim() ||
					env.VITE_CANISTER_ID_DEMO_CKUSDC_LEDGER?.trim(),
			),
		},
		indexes: {
			usdm: Boolean(env.VITE_CANISTER_ID_USDM_INDEX?.trim()),
			mmk: Boolean(env.VITE_CANISTER_ID_MMK_INDEX?.trim()),
		},
		gateway: Boolean(
			env.VITE_CANISTER_ID_AGENT_GATEWAY?.trim() ||
				env.VITE_AGENT_GATEWAY_CANISTER_ID?.trim(),
		),
		assistant: Boolean(
			env.VITE_PROTOCOL_ASSISTANT_CANISTER_ID?.trim() ||
				env.VITE_CANISTER_ID_PROTOCOL_ASSISTANT?.trim(),
		),
		faucet: Boolean(
			env.VITE_CANISTER_ID_DEMO_FAUCET?.trim() ||
				env.VITE_DEMO_FAUCET_CANISTER_ID?.trim(),
		),
		mode: String(env.MODE ?? env.VITE_APP_ENV ?? ""),
		isViteDev: Boolean(import.meta.env.DEV),
	};
}

let logged = false;
export function logRuntimeDiagnosticsOnce(): void {
	if (logged || typeof console === "undefined") return;
	logged = true;
	const d = getRuntimeDiagnostics();
	console.info("[motokomaker-v2] runtime diagnostics", {
		hasProtocolEnv: d.hasProtocolEnv,
		protocolId: d.protocolId || "(empty)",
		host: d.host,
		network: d.network,
		fetchRootKey: d.fetchRootKey,
		certVerifyFailure: d.certVerifyFailure,
		ledgers: d.ledgers,
		indexes: d.indexes,
		gateway: d.gateway,
		assistant: d.assistant,
		faucet: d.faucet,
		mode: d.mode,
		isViteDev: d.isViteDev,
	});
}
